import React from 'react';
import I18n from 'i18n-js';
import { useQuery } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import Modal from '@/components/ui/Modal';
import { Button } from '@/components/ui/Buttons';
import Icon from '@/components/ui/Icon';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/Table';
import Text from '@/components/ui/Text';
import QueryKeys from '@/utils/QueryKeys';
import cn from '@/utils/cn';
import { fetchQuestionScores, scoreColorClass, tagDescription } from './actions';
import Loading from './Loading';
import { FrameworkNoun, QuestionScore, SelectedScore } from './types';

const truncate = (text: string) => (text.length <= 32 ? text : `${text.substring(0, 31)}...`);

const NewTabLink = ({ children, href }: { children: React.ReactNode, href: string }) => (
  <a className="tw-inline-flex tw-items-center tw-gap-2" href={href} rel="noreferrer" target="_blank">
    {children}
    <Icon icon="ExternalLink" size={14} />
  </a>
);

const QuestionRow = ({ question }: { question: QuestionScore }) => (
  <TableRow className="tw-border-0 tw-border-b tw-border-solid tw-border-light-grey">
    <TableCell>{truncate(question.body)}</TableCell>
    <TableCell>
      <NewTabLink href={question.assessment.href}>{question.assessment.name}</NewTabLink>
    </TableCell>
    <TableCell>{question.location}</TableCell>
    <TableCell>{`${question.question_answer.score.split('.')[0]}/${question.points}`}</TableCell>
  </TableRow>
);

interface PerformanceModalProps {
  classroomId: number;
  frameworkNoun: FrameworkNoun;
  programId: number;
  selected: SelectedScore | null;
  onClose: () => void;
}

const PerformanceModal = ({ classroomId, frameworkNoun, programId, selected, onClose }: PerformanceModalProps) => {
  const studentId = selected?.student.id;
  const tagId = selected?.tag.id;

  const { data: questions, isLoading } = useQuery(
    [QueryKeys.StandardsReportQuestionScores, classroomId, studentId, tagId],
    () => fetchQuestionScores(classroomId, studentId, tagId),
    { enabled: !!selected },
  );

  if (!selected) return null;

  const { score, student, tag } = selected;
  const reviewHref = `${Routes.state_shared_program_correlations_path(programId)}#${tag.alias}`;
  const reviewKey = frameworkNoun === 'Benchmarks'
    ? 'standards_report_review_benchmarks'
    : 'standards_report_review_standards';

  return (
    <Modal
      isOpen
      nearlyFullScreen
      close={onClose}
      headerText={I18n.t('standards_report_student_performance', { name: student.name })}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-4">
        <div className="tw-flex-1">
          <Text className="tw-mb-2.5">
            <b>{tag.alias}</b>
            {' '}
            {tagDescription(tag)}
          </Text>
          <NewTabLink href={reviewHref}>{I18n.t(reviewKey)}</NewTabLink>
        </div>
        <Text className="tw-flex tw-items-center tw-gap-1 tw-m-0" weight="bold">
          {I18n.t('standards_report_result')}
          <span className={cn('tw-px-2 tw-py-1 tw-rounded', scoreColorClass(score))}>{`${score}%`}</span>
        </Text>
      </div>

      <Table className="tw-w-full tw-border-collapse tw-text-sm">
        <TableHeader className="tw-bg-light-grey">
          <TableRow>
            <TableCell header className="tw-text-left tw-w-1/4" scope="col">{I18n.t('question')}</TableCell>
            <TableCell header className="tw-text-left tw-w-1/4" scope="col">{I18n.t('assessment')}</TableCell>
            <TableCell header className="tw-text-left tw-w-1/3" scope="col">{I18n.t('location')}</TableCell>
            <TableCell header className="tw-text-left" scope="col">{I18n.t('score')}</TableCell>
          </TableRow>
        </TableHeader>
        {!isLoading && (
          <TableBody>
            {questions?.map(question => <QuestionRow key={question.id} question={question} />)}
          </TableBody>
        )}
      </Table>
      {isLoading && <Loading className="tw-mt-8" size={50} />}

      <hr />
      <footer className="tw-flex tw-justify-end">
        <Button onClick={onClose}>{I18n.t('close')}</Button>
      </footer>
    </Modal>
  );
};

export default PerformanceModal;
